import type { ItemDefInput } from '../../schemas/items';
import { NEIGHBORHOOD_STORIES } from '../neighborhood';

/** Complete small-room collections; the final heirloom of each story is never sold. */
function piece(id: ItemDefInput['id'], overrides: Partial<ItemDefInput>): ItemDefInput {
  const tea = id.includes('_tea_');
  const kind = id.startsWith('fur_') ? 'furniture' : 'decor';
  return {
    id,
    kind,
    nameKey: `item.${id}.name`,
    loreKey: `item.${id}.lore`,
    shopTab: kind === 'furniture' ? 'furniture' : 'decor',
    layer: 'object',
    footprint: { w: 1, h: 1 },
    rotations: 2,
    tags: ['heritage'],
    homeTags: ['zone:corner'],
    themeTags: ['cozy'],
    setId: tea ? 'set_tea' : 'set_old_europe',
    material: 'wood',
    unlockId: 'unl_minimart',
    storyGate: { storyId: tea ? 'lam' : 'elias', stage: 2 },
    buyPrice: 90,
    sources: [
      { kind: 'catalog' },
      ...NEIGHBORHOOD_STORIES.flatMap((story) =>
        story.steps.flatMap((step, stage) =>
          step.rewards.some((reward) => reward.type === 'item' && reward.itemId === id)
            ? [{ kind: 'reward' as const, ref: `neighborhood:${story.id}:${stage}` }]
            : [],
        ),
      ),
    ],
    sprite: { frame: `item_${id}` },
    ...overrides,
  };
}
const surface: NonNullable<ItemDefInput['slots']> = {
  count: 3,
  surfaceTag: 'table',
  accepts: [{ kinds: ['decor'], layers: ['surface'] }],
};

export const HERITAGE_FURNITURE: readonly ItemDefInput[] = [
  piece('fur_tea_table', {
    footprint: { w: 2, h: 1 },
    tags: ['table', 'tea'],
    slots: surface,
    buyPrice: 120,
    storyGate: { storyId: 'lam', stage: 1 },
  }),
  piece('fur_tea_chair', {
    tags: ['chair', 'seat'],
    homeTags: ['near:table'],
    buyPrice: 55,
    storyGate: { storyId: 'lam', stage: 1 },
  }),
  piece('fur_tea_screen', {
    footprint: { w: 2, h: 1 },
    tags: ['screen'],
    homeTags: ['wall-adjacent'],
    buyPrice: 140,
  }),
  piece('fur_tea_cabinet', {
    footprint: { w: 2, h: 1 },
    tags: ['bookshelf', 'storage'],
    homeTags: ['wall-adjacent'],
    buyPrice: undefined,
    unlockId: undefined,
    storyGate: undefined,
    sources: [{ kind: 'reward', ref: 'neighborhood:lam:3' }],
  }),
  piece('fur_europe_table', {
    footprint: { w: 2, h: 1 },
    tags: ['table'],
    slots: surface,
    buyPrice: 130,
    storyGate: { storyId: 'elias', stage: 1 },
  }),
  piece('fur_europe_chair', {
    tags: ['chair', 'seat'],
    homeTags: ['near:table'],
    material: 'fabric',
    buyPrice: 60,
    storyGate: { storyId: 'elias', stage: 1 },
  }),
  piece('fur_europe_cupboard', {
    footprint: { w: 2, h: 1 },
    tags: ['bookshelf', 'storage'],
    homeTags: ['wall-adjacent'],
    buyPrice: 150,
  }),
  piece('fur_europe_fireplace', {
    footprint: { w: 2, h: 1 },
    tags: ['fireplace'],
    homeTags: ['wall-adjacent'],
    buyPrice: 180,
    storyGate: { storyId: 'elias', stage: 3 },
  }),
  piece('fur_europe_keepsake_chair', {
    tags: ['chair', 'seat'],
    homeTags: ['near:table'],
    buyPrice: undefined,
    unlockId: undefined,
    storyGate: undefined,
    sources: [{ kind: 'reward', ref: 'neighborhood:elias:3' }],
  }),
];
export const HERITAGE_DECOR: readonly ItemDefInput[] = [
  piece('dec_tea_set', {
    layer: 'surface',
    footprint: undefined,
    material: 'ceramic',
    homeTags: ['on:table'],
    rotations: 1,
    buyPrice: 45,
    storyGate: { storyId: 'lam', stage: 1 },
  }),
  piece('dec_tea_lantern', { tags: ['lamp', 'light'], material: 'paper', rotations: 1, buyPrice: 75 }),
  piece('dec_tea_vase', {
    layer: 'surface',
    footprint: undefined,
    material: 'ceramic',
    homeTags: ['on:table', 'on:bookshelf'],
    rotations: 1,
    buyPrice: 45,
  }),
  piece('dec_tea_window', {
    layer: 'wall',
    footprint: undefined,
    wallSpan: 2,
    homeTags: ['wall'],
    rotations: 1,
    buyPrice: 100,
  }),
  piece('dec_tea_rug', {
    layer: 'floor',
    footprint: { w: 2, h: 2 },
    tags: ['rug'],
    material: 'fabric',
    rotations: 1,
    buyPrice: 85,
    storyGate: { storyId: 'lam', stage: 3 },
  }),
  piece('dec_tea_scroll', {
    layer: 'wall',
    footprint: undefined,
    wallSpan: 1,
    material: 'paper',
    homeTags: ['wall'],
    rotations: 1,
    buyPrice: 55,
    storyGate: { storyId: 'lam', stage: 3 },
  }),
  piece('dec_europe_candles', {
    layer: 'surface',
    footprint: undefined,
    tags: ['light'],
    material: 'metal',
    homeTags: ['on:table'],
    rotations: 1,
    buyPrice: 45,
    storyGate: { storyId: 'elias', stage: 1 },
  }),
  piece('dec_europe_books', {
    layer: 'surface',
    footprint: undefined,
    material: 'paper',
    homeTags: ['on:table', 'on:bookshelf'],
    rotations: 1,
    buyPrice: 40,
  }),
  piece('dec_europe_vase', {
    layer: 'surface',
    footprint: undefined,
    material: 'ceramic',
    homeTags: ['on:table'],
    rotations: 1,
    buyPrice: 50,
  }),
  piece('dec_europe_rug', {
    layer: 'floor',
    footprint: { w: 2, h: 2 },
    tags: ['rug'],
    material: 'fabric',
    rotations: 1,
    buyPrice: 90,
    storyGate: { storyId: 'elias', stage: 3 },
  }),
  piece('dec_europe_painting', {
    layer: 'wall',
    footprint: undefined,
    wallSpan: 1,
    material: 'paper',
    homeTags: ['wall'],
    rotations: 1,
    buyPrice: 60,
    storyGate: { storyId: 'elias', stage: 3 },
  }),
];
